import React, { useEffect, useState } from 'react';
import update from 'immutability-helper';

import AltFieldMapper, { AltFieldItem, validateRequiredFields } from 'views/AltFieldMapper.js';
import Appearance from 'styles/Appearance.js';
import Layer, { LayerItemSpacing } from 'structure/Layer.js';
import ListField from 'views/ListField.js';
import Request from 'files/Request.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';
import Views from 'views/Main.js';

const AddEditLegacyMessage = ({ config, isNewTarget, message, onChange }, { index, options, utils }) => {

    const layerID = isNewTarget ? 'new_message' : `edit_message_${message.id}`;
    const [edits, setEdits] = useState(message || {});
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [methods, setMethods] = useState([]);

    const onDoneClick = async () => {
        try {

            // verify that required variables have been provided
            await validateRequiredFields(getFields);

            // prepare target method prevent moving forward if no method has been selected
            let target = methods.find(item => item.id === edits.method);
            if(!target) {
                throw new Error('Please select a communication method from the list');
            }

            // prepare payload for sender 
            let payload = {
                ...edits,
                title: `${target.title}: ${edits.contacts?.length || 0} ${edits.contacts?.length === 1 ? 'Recipient' : 'Recipients'}`
            }

            // close layer and notify subscribers of data change
            setLayerState('close');
            if(typeof(onChange) === 'function') {
                onChange(payload);
            }
            
        } catch(e) {
            utils.alert.show({
                error: e,
                message: e.message,
                origin: 'add_edit_legacy_message.on_done_click'
            });
        }
    }

    const onUpdateTarget = props => {
        setEdits(edits => ({ ...edits, ...props }));
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: setLayerState.bind(this, 'close'),
            text: 'Close'
        },{
            color: 'primary',
            id: 'done',
            onClick: onDoneClick,
            text: 'Done'
        }];
    }

    const getFields = () => {
        
        // prepare default fields
        let fields = [{
            component: 'list',
            items: methods,
            loading: loading,
            onChange: item => onUpdateTarget({ 
                contacts: null,
                content: null,
                method: item?.id 
            }),
            title: 'Communication Method',
            value: methods.find(item => item.id === edits.method)?.title
        }];

        // add additional fields if a method has been selected
        let target = methods.find(item => item.id === edits.method);
        if(target) {
            fields = fields.concat([{
                component: target.component?.key,
                key: 'content',
                onChange: val => onUpdateTarget({ content: val }),
                title: target.title,
                value: edits.content
            },{
                component: 'contact_picker',
                key: 'contacts',
                onChange: results => onUpdateTarget({ contacts: results }),
                props: {
                    config: { 
                        components: target.props?.components 
                    }
                },
                title: 'Recipients Of Legacy Message',
                value: edits.contacts
            }]);
        }
        return [{
            key: 'details',
            items: fields,
            title: 'Legacy Message Configuration'
        }];
    }

    const fetchResources = async () => {
        try {
        
            // submit request to server
            setLoading(true);
            let { methods } = await Request.get(utils, '/subscriptions/', {
                provider: config?.provider,
                type: 'legacy_messaging_resources'
            });

            setLoading(false);
            setMethods(methods || []);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving the legacy messaging resources. ${e.message || 'An unknown error occurred'}`,
                origin: 'legacy_message_editor.fetch_resources'
            });
        }
    }
    
    useEffect(() => {
        fetchResources();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true
        }}>
            <AltFieldMapper
            fields={getFields()}
            utils={utils} />
        </Layer>
    )
}

const LegacyMessageEditor = ({ config, onChange, onFocus, utils, value }) => {

    const [values, setValues] = useState([]);

    const onFieldClick = () => {
        utils.layer.open({
            Component: AddEditLegacyMessage.bind(this, { 
                config,
                isNewTarget: true,
                onChange: result => {

                    // add value and update local state
                    let next = update(values, { $push: [result] });
                    setValues(next);

                    // notify subscribers of data change
                    if(typeof(onChange) === 'function') {
                        onChange(next);
                    }
                },
            }),
            id: 'new_message'
        });
    }

    const onMessageClick = item => {

        let message = values.find(value => value.id === item.id);
        utils.layer.open({
            Component: AddEditLegacyMessage.bind(this, {
                config,
                isNewTarget: false,
                onChange: result => {

                    // update value entry with local state
                    let next = values.map(value => value.id === result.id ? result : value);
                    setValues(next);

                    // notify subscribers of data change
                    if(typeof(onChange) === 'function') {
                        onChange(next);
                    }
                },
                message
            }),
            id: `edit_message_${item.id}`
        });
    }

    const onRemoveItem = (index, evt) => {

        // stop parent click from firing
        evt.stopPropagation();

        // update local state with desired items
        let next = update(values, { $splice: [[ index, 1 ]] });
        setValues(next);

        // notify subscriber of data change
        if(typeof(onChange) === 'function') {
            onChange(next);
        }
    }

    useEffect(() => {
        setValues(value || []);
    }, [value]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        }}>
            <TextField
            onClick={onFieldClick}
            onFocus={onFocus}
            placeholder={`${Utils.isMobile() ? 'Tap' : 'Click'} to configure content...`}
            utils={utils} />
            {values.length > 0 && (
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    marginTop: 8,
                    width: '100%'
                }}>
                    {values.map((value, index) => {
                        return (
                            <div key={index}>
                                {Views.row({
                                    bottomBorder: index !== values.length - 1,
                                    hideOnClickArrow: true,
                                    label: value.title || 'Untitled Message',
                                    onClick: onMessageClick.bind(this, value),
                                    value: (
                                        <img
                                        className={'text-button'}
                                        onClick={onRemoveItem.bind(this, index)}
                                        src={'images/delete-red-icon.png'}
                                        style={{
                                            height: 20,
                                            objectFit: 'contain',
                                            width: 20
                                        }} />
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default LegacyMessageEditor;
